import type { AgencyBrandDto, ImageDto } from "@tripcart/shared";

/**
 * The operator's uploaded logo, or null when they have not set one.
 *
 * `AgencyBrandDto.logo` is a required field, so an agency without a logo
 * carries a placeholder image rather than an absent one. Every surface that
 * renders the brand has to know that a placeholder is not a logo — the header
 * would otherwise show a grey 1200×630 box, and the Organization JSON-LD would
 * hand Google a placehold.co URL as the brand mark. One resolver so the
 * visible header and the structured data can never disagree about it.
 */
/**
 * The stand-in image host — `BLANK_IMAGE` in `lib/tenant.ts` is the only one
 * this repo produces, so the set holds exactly it. Matched on HOST, never as a
 * substring of the whole
 * URL: an uploaded file's storage key ends in its own sanitised filename
 * (`agencies/<id>/<id>/<name>`), so a substring test read a logo genuinely
 * named `logo-placeholder.png` as "no logo" and silently dropped it from both
 * the header and the Organization JSON-LD — while Site settings, which reads
 * the attachment directly, still showed it. A filename cannot change the host.
 */
const PLACEHOLDER_HOSTS = new Set(["placehold.co"]);

export function isPlaceholderUrl(url: string): boolean {
  try {
    return PLACEHOLDER_HOSTS.has(new URL(url).hostname.toLowerCase());
  } catch {
    // Not absolute, so not one of the stand-ins above. `new URL` throws on a
    // relative path rather than returning null, and the operator's own image is
    // the safer reading of an address we cannot parse.
    return false;
  }
}

export function resolveAgencyLogo(agency: AgencyBrandDto): ImageDto | null {
  const url = agency.logo?.url.trim() ?? "";
  if (!url || isPlaceholderUrl(url)) return null;
  return agency.logo;
}
